import React, { useState, useCallback } from "react"
import SparkMD5 from "spark-md5"

interface UseFileUploadOptions {
  onUpload: (signedId: string, file: File) => Promise<void>
  acceptedTypes?: string[]
  maxSizeBytes?: number
  calculateChecksum?: boolean
  onError?: (error: Error) => void
  initialFileName?: string | null
}

interface UseFileUploadReturn {
  uploading: boolean
  uploadedFile: string | null
  error: string | null
  uploadFile: (file: File) => Promise<void>
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>
  reset: () => void
  FileInput: React.ComponentType<{ id?: string; className?: string }>
  triggerUpload: () => void
  setUploadedFile: React.Dispatch<React.SetStateAction<string | null>>
}

export const useFileUpload = ({
  onUpload,
  acceptedTypes = [],
  maxSizeBytes,
  calculateChecksum = true,
  onError,
  initialFileName = null,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const [uploading, setUploading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<string | null>(
    initialFileName,
  )
  const [error, setError] = useState<string | null>(null)

  const calculateMD5 = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      const spark = new SparkMD5.ArrayBuffer()

      reader.onload = () => {
        spark.append(reader.result as ArrayBuffer)
        const hash = spark.end()
        const hashBase64 = btoa(
          hash
            .match(/[\da-f]{2}/gi)!
            .map((h) => String.fromCharCode(parseInt(h, 16)))
            .join(""),
        )
        resolve(hashBase64)
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsArrayBuffer(file)
    })
  }, [])

  const getDirectUploadUrl = useCallback(
    async (file: File) => {
      const payload: any = {
        blob: {
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
        },
      }

      if (calculateChecksum) {
        payload.blob.checksum = await calculateMD5(file)
      }

      const response = await fetch("/storage/direct_uploads", {
        method: "POST",
        headers: {
          "X-CSRF-Token":
            (
              document.querySelector(
                'meta[name="csrf-token"]',
              ) as HTMLMetaElement
            )?.content || "",
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(
          `Failed to get direct upload URL (Status: ${response.status})`,
        )
      }

      return response.json()
    },
    [calculateChecksum, calculateMD5],
  )

  const uploadToS3 = useCallback(async (file: File, directUploadData: any) => {
    const response = await fetch(directUploadData.direct_upload.url, {
      method: "PUT",
      headers: directUploadData.direct_upload.headers,
      body: file,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`S3 Upload failed: ${errorText}`)
    }

    return directUploadData.signed_id
  }, [])

  const validateFile = useCallback(
    (file: File): void => {
      if (maxSizeBytes && file.size > maxSizeBytes) {
        throw new Error(
          `File size exceeds limit of ${Math.round(
            maxSizeBytes / 1024 / 1024,
          )}MB`,
        )
      }

      if (acceptedTypes.length > 0) {
        const fileExtension = "." + file.name.split(".").pop()?.toLowerCase()
        if (!acceptedTypes.includes(fileExtension)) {
          throw new Error(
            `File type not accepted. Allowed types: ${acceptedTypes.join(
              ", ",
            )}`,
          )
        }
      }
    },
    [acceptedTypes, maxSizeBytes],
  )

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        setUploading(true)
        setError(null)

        validateFile(file)

        const directUploadData = await getDirectUploadUrl(file)
        const signedId = await uploadToS3(file, directUploadData)

        await onUpload(signedId, file)

        setUploadedFile(file.name)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Upload failed"
        setError(errorMessage)
        onError?.(err instanceof Error ? err : new Error(errorMessage))
        throw err
      } finally {
        setUploading(false)
      }
    },
    [onUpload, validateFile, getDirectUploadUrl, uploadToS3, onError],
  )

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      try {
        await uploadFile(file)
      } finally {
        // Reset input to allow selecting the same file again
        event.target.value = ""
      }
    },
    [uploadFile],
  )

  const reset = useCallback(() => {
    setUploadedFile(null)
    setError(null)
    setUploading(false)
  }, [])

  // File input component that can be styled and positioned anywhere
  const FileInput: React.ComponentType<{ id?: string; className?: string }> =
    useCallback(
      ({ id = "file-upload", className }) => (
        <input
          id={id}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleFileChange}
          disabled={uploading}
          style={{ display: "none" }}
          className={className}
        />
      ),
      [acceptedTypes, handleFileChange, uploading],
    )

  const triggerUpload = useCallback(() => {
    const input = document.getElementById("file-upload") as HTMLInputElement
    input?.click()
  }, [])

  return {
    uploading,
    uploadedFile,
    error,
    uploadFile,
    handleFileChange,
    reset,
    FileInput,
    triggerUpload,
    setUploadedFile,
  }
}
